import { computed, effect, Injectable, signal, untracked } from '@angular/core';
import { PosCatalogProductDto } from '@klubovna/api-client';
import { CartData, CartSummary, DrinkIngredientModification, LocalCartItem, PaymentType } from '@posTypes';

const CART_LABEL_PREFIX = 'Cart';
const STORAGE_KEY_PREFIX = 'pos_carts_';
const INITIAL_CART_COUNTER = 1;

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private persistenceKey: string | null = null;

  private carts = signal<Map<string, CartData>>(this.createInitialCarts());
  private activeCartId = signal<string>(this.carts().keys().next().value!);
  private cartCounter = signal(INITIAL_CART_COUNTER);

  private activeCart = computed(() => this.carts().get(this.activeCartId())!);

  readonly items = computed(() => Array.from(this.activeCart().items.values()));

  readonly itemsCount = computed(() => {
    return this.items().reduce((count, item) => count + item.quantity, 0);
  });

  readonly totalCents = computed(() => {
    return this.items()
      .filter(item => item.paymentType === PaymentType.Money)
      .reduce((total, item) => total + item.product.priceCents * item.quantity, 0);
  });

  readonly totalPoints = computed(() => {
    return this.items()
      .filter(item => item.paymentType === PaymentType.Points)
      .reduce((total, item) => total + item.product.pointsCost * item.quantity, 0);
  });

  readonly activeCartLabel = computed(() => this.activeCart().label);

  readonly parkedCarts = computed<CartSummary[]>(() => {
    const currentActiveId = this.activeCartId();
    return Array.from(this.carts().values())
      .filter(cart => cart.id !== currentActiveId)
      .sort((a, b) => a.createdAt - b.createdAt)
      .map(cart => ({
        id: cart.id,
        label: cart.label,
        itemCount: Array.from(cart.items.values()).reduce((sum, item) => sum + item.quantity, 0),
        totalCents: Array.from(cart.items.values())
          .filter(item => item.paymentType === PaymentType.Money)
          .reduce((sum, item) => sum + item.product.priceCents * item.quantity, 0),
      }));
  });

  readonly hasParkedCarts = computed(() => this.parkedCarts().length > 0);

  constructor() {
    effect(() => {
      this.carts();
      this.activeCartId();
      this.cartCounter();
      untracked(() => this.saveToStorage());
    });
  }

  addItem(product: PosCatalogProductDto, paymentType: PaymentType = PaymentType.Money): void {
    if (!product.isActive) return;

    this.updateActiveCartItems(items => {
      const key = `${ product.id }_${ paymentType }`;
      const existing = items.get(key);

      if (existing) {
        items.set(key, { ...existing, quantity: existing.quantity + 1 });
      } else {
        items.set(key, { product, quantity: 1, paymentType });
      }
    });
  }

  addCompositeItem(
    product: PosCatalogProductDto,
    ingredientModifications: DrinkIngredientModification[],
    modificationNote: string,
    calculatedPriceCents: number,
    paymentType: PaymentType = PaymentType.Money,
  ): void {
    if (!product.isActive) return;

    const compositeKey = `${ product.id }_${ paymentType }_${ crypto.randomUUID() }`;
    this.updateActiveCartItems(items => {
      items.set(compositeKey, {
        product: { ...product, priceCents: calculatedPriceCents },
        quantity: 1,
        paymentType,
        isComposite: true,
        ingredientModifications,
        modificationNote,
      });
    });
  }

  incrementItem(item: LocalCartItem): void {
    const key = `${ item.product.id }_${ item.paymentType }`;
    this.updateActiveCartItems(items => {
      const existing = items.get(key);
      if (existing) {
        items.set(key, { ...existing, quantity: existing.quantity + 1 });
      }
    });
  }

  decrementItem(item: LocalCartItem): void {
    const key = `${ item.product.id }_${ item.paymentType }`;
    this.updateActiveCartItems(items => {
      const existing = items.get(key);
      if (existing) {
        if (existing.quantity > 1) {
          items.set(key, { ...existing, quantity: existing.quantity - 1 });
        } else {
          items.delete(key);
        }
      }
    });
  }

  getProductQuantity(productId: string): number {
    return this.items()
      .filter(item => item.product.id === productId)
      .reduce((total, item) => total + item.quantity, 0);
  }

  setItems(items: LocalCartItem[]): void {
    const newItems = new Map<string, LocalCartItem>();
    items.forEach(item => {
      const key = `${ item.product.id }_${ item.paymentType }`;
      const existing = newItems.get(key);
      if (existing) {
        newItems.set(key, { ...existing, quantity: existing.quantity + item.quantity });
      } else {
        newItems.set(key, item);
      }
    });

    this.carts.update(currentCarts => {
      const updated = new Map(currentCarts);
      const activeId = this.activeCartId();
      const activeCart = updated.get(activeId)!;
      updated.set(activeId, { ...activeCart, items: newItems });
      return updated;
    });
  }

  clear(): void {
    this.updateActiveCartItems(items => items.clear());
  }

  parkCart(): void {
    const currentItems = this.activeCart().items;
    if (currentItems.size === 0) return;

    const newCounter = this.cartCounter() + 1;
    this.cartCounter.set(newCounter);

    const newCart = this.createCart(newCounter);

    this.carts.update(currentCarts => {
      const updated = new Map(currentCarts);
      updated.set(newCart.id, newCart);
      return updated;
    });

    this.activeCartId.set(newCart.id);
  }

  switchToCart(id: string): void {
    if (this.carts().has(id)) {
      this.activeCartId.set(id);
    }
  }

  deleteParkedCart(id: string): void {
    if (id === this.activeCartId()) return;
    if (!this.carts().has(id)) return;

    this.carts.update(currentCarts => {
      const updated = new Map(currentCarts);
      updated.delete(id);
      return updated;
    });
  }

  deleteActiveCart(): void {
    const currentActiveId = this.activeCartId();

    this.carts.update(currentCarts => {
      const updated = new Map(currentCarts);
      updated.delete(currentActiveId);

      if (updated.size === 0) {
        const newCounter = this.cartCounter() + 1;
        this.cartCounter.set(newCounter);
        const newCart = this.createCart(newCounter);
        updated.set(newCart.id, newCart);
        this.activeCartId.set(newCart.id);
      } else {
        this.activeCartId.set(updated.keys().next().value!);
      }

      return updated;
    });
  }

  clearAll(): void {
    this.clearStorage();
    const initialCarts = this.createInitialCarts();
    this.carts.set(initialCarts);
    this.activeCartId.set(initialCarts.keys().next().value!);
    this.cartCounter.set(INITIAL_CART_COUNTER);
  }

  setSessionId(sessionId: string): void {
    this.persistenceKey = `${ STORAGE_KEY_PREFIX }${ sessionId }`;
    this.loadFromStorage();
  }

  clearStorage(): void {
    if (this.persistenceKey) {
      localStorage.removeItem(this.persistenceKey);
    }
  }

  private createInitialCarts(): Map<string, CartData> {
    const cart = this.createCart(INITIAL_CART_COUNTER);
    return new Map([ [ cart.id, cart ] ]);
  }

  private createCart(counter: number): CartData {
    return {
      id: crypto.randomUUID(),
      label: `${ CART_LABEL_PREFIX } ${ counter }`,
      items: new Map(),
      createdAt: Date.now(),
    };
  }

  private updateActiveCartItems(mutator: (items: Map<string, LocalCartItem>) => void): void {
    this.carts.update(currentCarts => {
      const updated = new Map(currentCarts);
      const activeId = this.activeCartId();
      const activeCart = updated.get(activeId)!;
      const newItems = new Map(activeCart.items);
      mutator(newItems);
      updated.set(activeId, { ...activeCart, items: newItems });
      return updated;
    });
  }

  private saveToStorage(): void {
    if (!this.persistenceKey) return;

    const cartsArray = Array.from(this.carts().entries()).map(([ id, cart ]) => ({
      id,
      label: cart.label,
      createdAt: cart.createdAt,
      items: Array.from(cart.items.entries()),
    }));

    const data = {
      carts: cartsArray,
      activeCartId: this.activeCartId(),
      cartCounter: this.cartCounter(),
    };

    localStorage.setItem(this.persistenceKey, JSON.stringify(data));
  }

  private loadFromStorage(): void {
    if (!this.persistenceKey) return;

    const raw = localStorage.getItem(this.persistenceKey);
    if (!raw) return;

    try {
      const data = JSON.parse(raw);

      const carts = new Map<string, CartData>();
      for (const cart of data.carts) {
        carts.set(cart.id, {
          id: cart.id,
          label: cart.label,
          createdAt: cart.createdAt,
          items: new Map(cart.items),
        });
      }

      if (carts.size === 0) return;

      this.carts.set(carts);
      this.activeCartId.set(data.activeCartId);
      this.cartCounter.set(data.cartCounter);
    } catch {
      this.clearStorage();
    }
  }
}
